import { useSelector, useDispatch } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import { useMemo } from 'react';
import getBrochureTripId from '../helpers/getBrochureTripId';
import { TravelTypes } from '../constants';
import { addToBrochure } from '../store/slices/brochure';
import useBrochureOptionRemoval from './useBrochureOptionRemoval';
import formatStateroomsForRequest from '../helpers/formatters/formatStateroomsForRequest';

const useHandleCruiseAddToBrochure = ({
  tripId,
  itineraryDetail,
  brochure,
  isViewingBrochure,
  isBrochurePage,
  currentBrochure,
  brochureOnBrochurePage,
  cabinCategory,
  rateOption,
  selectedRoomIndex,
  tripRoomSearchInput,
}) => {
  const { track } = useAnalytics();
  const dispatch = useDispatch();
  const findRateOption = (rateOptions, rateOptionToFind) =>
    _.find(
      rateOptions,
      (c) =>
        c.rateOptionCode === rateOptionToFind.rateOptionCode &&
        c.rateOptionType === rateOptionToFind.rateOptionType &&
        c.nonRefundTypeCode === rateOptionToFind.nonRefundTypeCode,
    );

  const findRateCategory = (cabinCategories, cabinCategoryToFind) =>
    _.find(cabinCategories, (c) => c.cabinCategoryCode === cabinCategoryToFind.cabinCategoryCode);

  const findCruiseItinerary = (trip) =>
    _.find(trip.tripSegments, (i) => i.itinerary.mode === TravelTypes.cruise);
  const { staterooms } = useSelector((store) => store.tripSearch.searchObj);
  const getCabinCategory = () => ({
    roomIndex: selectedRoomIndex,
    searchInput: formatStateroomsForRequest(
      staterooms[selectedRoomIndex],
      itineraryDetail.cruiseLine.cruiseLineId,
    ),
    cabinCategories: [
      {
        ...cabinCategory,
        rateOptions: [
          {
            ...rateOption,
          },
        ],
      },
    ],
  });

  const id = useMemo(
    () =>
      getBrochureTripId(
        tripId,
        brochure,
        itineraryDetail,
        tripRoomSearchInput || staterooms,
        TravelTypes.cruise,
      ),
    [brochure, itineraryDetail, staterooms, tripRoomSearchInput, tripId],
  );

  const handleSegmentRemoval = useBrochureOptionRemoval();
  const handleAddToBrochureClick = (event) => {
    if (event && event.type === 'keydown' && event.key !== 'Enter') return;
    if (!cabinCategory || !rateOption || selectedRoomIndex === undefined) return;
    const cruiseKey = (itineraryDetail || {}).itineraryId;
    const handleSegmentRemovalWrapper = (brochureId, brochureToUpdate, isSavedToDatabase) => {
      handleSegmentRemoval({
        brochureId,
        currentBrochure: brochureToUpdate,
        travelType: TravelTypes.cruise,
        segmentKey: '',
        segmentKeyValue: cruiseKey,
        findTripSegmentfn: findCruiseItinerary,
        findRateCategoryfn: findRateCategory,
        findRateOptionfn: findRateOption,
        cabinCategory,
        rateOption,
        selectedRoomIndex,
        isSavedToDatabase,
        itinerary: itineraryDetail,
        staterooms: tripRoomSearchInput,
      });
    };
    if (isBrochurePage && brochureOnBrochurePage && tripId) {
      const brochureToUpdate = JSON.parse(JSON.stringify(brochureOnBrochurePage));
      handleSegmentRemovalWrapper(tripId, brochureToUpdate, true);
      return;
    }

    if (isViewingBrochure && !_.isNil(currentBrochure) && !_.isNil(brochure.id)) {
      const brochureId = brochure && brochure.id;
      const isSavedToDatabase = brochure && brochure.isSavedToDatabase;
      handleSegmentRemovalWrapper(brochureId, currentBrochure, isSavedToDatabase);
    }

    let updatedTrip = {
      tripSegments: [],
    };

    if (brochure && brochure.trips[id]) {
      // Clone brochure, we can not update brochure value
      updatedTrip = JSON.parse(JSON.stringify(brochure.trips[id]));
    }
    // Add/Update existing cruise itinerary
    let action = 'add';
    const cruiseSeg = findCruiseItinerary(updatedTrip);
    if (!cruiseSeg) {
      action = 'add';
      // Add cruise itinerary
      updatedTrip.tripSegments.push({
        itinerary: {
          mode: TravelTypes.cruise,
          itineraryDetail,
          cabinCategories: [getCabinCategory()],
        },
      });
    } else if (
      cruiseSeg.itinerary.cabinCategories &&
      (!cruiseSeg.itinerary.cabinCategories[selectedRoomIndex] ||
        (cruiseSeg.itinerary.cabinCategories[selectedRoomIndex].cabinCategories || []).length === 0)
    ) {
      action = 'update_add';
      cruiseSeg.itinerary.cabinCategories[selectedRoomIndex] = getCabinCategory();
    } else {
      // update existing cruise itinerary
      const selectedCategory = findRateCategory(
        cruiseSeg.itinerary.cabinCategories[selectedRoomIndex].cabinCategories,
        cabinCategory,
      );
      if (selectedCategory != null) {
        const selectedRate = findRateOption(selectedCategory.rateOptions, rateOption);
        if (selectedRate) {
          // Remove rate option from category
          _.remove(selectedCategory.rateOptions, (r) => r === selectedRate);
          action = 'update_remove';

          // If no rate options in category, remove category
          if (selectedCategory.rateOptions.length === 0) {
            _.remove(
              cruiseSeg.itinerary.cabinCategories[selectedRoomIndex].cabinCategories,
              (r) => r === selectedCategory,
            );
          }
        } else {
          // Add rate option to category
          selectedCategory.rateOptions.push(rateOption);
          action = 'update_add';
        }
      } else {
        // Add cabin category
        action = 'update_add';
        cruiseSeg.itinerary.cabinCategories[selectedRoomIndex].cabinCategories.push({
          ...cabinCategory,
          rateOptions: [
            {
              ...rateOption,
            },
          ],
        });
      }
    }
    if (
      cruiseSeg &&
      cruiseSeg.itinerary.cabinCategories?.every(
        (room) => !room || !room.cabinCategories || room.cabinCategories.length === 0,
      )
    ) {
      action = 'remove';
    }
    dispatch(
      addToBrochure({
        tripId: id,
        updatedTrip,
        staterooms,
      }),
    );
    track(`CruiseBrochure.${action}`, {
      cruiseKey,
      cabinCategory: cabinCategory?.cabinCategoryCode,
      rateOption: rateOption?.rateOptionCode,
    });
  };
  return handleAddToBrochureClick;
};

export default useHandleCruiseAddToBrochure;
